import { ApiClient } from '@/config/axios.config';
import { CurrentUser, CurrentUserResponse, deserializeCurrentUser } from '@/models/CurrentUser';

/**
 * Current User Service
 * API service for fetching and managing current user data
 * Implements caching to avoid redundant API calls
 */
class CurrentUserService {
  /**
   * Get current authenticated user
   * Always fetches from API (users endpoints are excluded from caching)
   */
  async getMe(forceRefresh = false): Promise<CurrentUser> {
    void forceRefresh;

    try {
      const response = await ApiClient.get<CurrentUserResponse>('/users/me');
      return deserializeCurrentUser(response);
    } catch (error) {
      console.error('[CurrentUserService] Failed to fetch user:', error);
      throw error;
    }
  }

  /**
   * Update current user profile
   * Endpoint: PUT /users/profile
   * Updates: name, whatsAppNumber, and notification preferences (Monday-Friday)
   */
  async updateMe(updates: Partial<CurrentUser>): Promise<CurrentUser> {
    try {
      await ApiClient.put('/users/profile', updates);

      // Refresh user data after update
      const response = await ApiClient.get<CurrentUserResponse>('/users/me');
      return deserializeCurrentUser(response);
    } catch (error) {
      console.error('[CurrentUserService] Failed to update user:', error);
      throw error;
    }
  }

  /**
   * Accept invitation and get user data
   * Special endpoint for invitation acceptance flow
   */
  async getMeAndAcceptInvite(): Promise<CurrentUser> {
    try {
      const response = await ApiClient.post<CurrentUserResponse>('/users/me/accept-invite');
      return deserializeCurrentUser(response);
    } catch (error) {
      console.error('[CurrentUserService] Failed to accept invite:', error);
      throw error;
    }
  }

  /**
   * Clear cache (useful for logout)
   */
  clearCache(): void {
    // No-op: users endpoints are intentionally uncached
  }

  /**
   * Get cached user without making API call
   */
  getCachedUser(): CurrentUser | null {
    return null;
  }
}

// Export singleton instance
export const currentUserService = new CurrentUserService();
